import { Button, Dropdown, Modal } from 'antd'
import { observer } from 'mobx-react-lite'
import { FaRegListAlt } from 'react-icons/fa'
import { GiConfirmed } from 'react-icons/gi'
import { IoMdMore } from 'react-icons/io'
import { MdDomainVerification, MdOutlineCancel, MdOutlinePlaylistRemove } from 'react-icons/md'

import {
    CRM_VERIFICATION_BUTTON_LABELS as LABELS,
    CRM_VERIFICATION_TEXTS as T,
    CRM_VERIFICATION_TRANSITIONS,
} from './CrmVerification'
import { RejectVerificationModal } from './RejectVerificationModal'
import { verificationStoreFor } from './VerificationStore'
import { VerifyModal } from './VerifyModal'

import type { CrmVerificationOwnerConfig, CrmVerificationPermissions, CrmVerificationTransition } from './CrmVerification'
import type { VerificationCrudContextLike, VerificationStore } from './VerificationStore'
import type { MenuProps } from 'antd'
import type { ReactNode } from 'react'

/** Параметри обробника `detailConfig.modeConfig.view.onButtons` ядра. */
export interface VerificationButtonsParams {
    buttons: Record<string, unknown>
    crudContext?: unknown
    content?: unknown[]
}

interface ButtonDeps {
    formContext?: { isEditable?: boolean | undefined; values?: Record<string, unknown> | undefined } | undefined
}

/** Іконки й тон кнопок (`SPEC-008` §7.2) — як у легасі `useButtons4DrawerExtra.jsx`. */
const BUTTONS: Record<CrmVerificationTransition, { icon: ReactNode; danger: boolean }> = {
    send_to_verification: { icon: <MdDomainVerification />, danger: false },
    verify: { icon: <GiConfirmed />, danger: false },
    reject_verification: { icon: <MdOutlineCancel />, danger: true },
    blacklist: { icon: <FaRegListAlt />, danger: true },
    unblacklist: { icon: <MdOutlinePlaylistRemove />, danger: true },
}

const permissionsOf = (deps: ButtonDeps): CrmVerificationPermissions => {
    const permissions = deps.formContext?.values?.['_permissions']

    return permissions !== null && typeof permissions === 'object' ? (permissions as CrmVerificationPermissions) : {}
}

/**
 * Переходи, які показує картка: лише в перегляді й лише дозволені
 * `_permissions` запису — у порядку рушія.
 */
const allowedTransitions = (deps: ButtonDeps): CrmVerificationTransition[] => {
    if (deps.formContext?.isEditable === true) return []

    const permissions = permissionsOf(deps)

    return CRM_VERIFICATION_TRANSITIONS.filter((transition) => permissions[transition] === true)
}

/**
 * Що меню бере з картки — загорнуте в звичайний (не MobX) клас.
 *
 * ⚠️ Елемент `children` ядрова `ButtonGroup` кладе в глибоко спостережуваний
 * `ButtonGroupStore` і віддає назад через `toJS`. Спостережуваний об'єкт у
 * пропсах (`VerificationStore`, ядровий `formContext`) вертається звідти
 * простою копією: без методів (`store.start is not a function`) і без
 * реактивності. Екземпляр звичайного класу MobX не чіпає — і не клонує.
 */
class VerificationMenuSource {
    constructor(
        readonly store: VerificationStore,
        readonly deps: ButtonDeps
    ) {}
}

interface VerificationActionsMenuProps {
    source: VerificationMenuSource
}

/**
 * «⋮» з переходами верифікації — власний antd-`Dropdown` на antd-кнопці.
 *
 * ⚠️ Не `priority: 'dropdown'` ядрової `ButtonGroup`: її «⋮» — це ядровий
 * `Button`, а він на **кожен** рендер створює новий тип компонента
 * (`UIManager.render()` → `observer(...)`), тож React не оновлює кнопку, а
 * перемонтовує її. Випадний список, відкриваючись, передає тригеру нові
 * пропси → тригер перемонтовується → список втрачає якір і знову
 * перераховує позицію → ще рендер. Цикл кожні ~30 мс: меню так і висить
 * за межами екрана (позиція «prepare» antd, −12800 × −7200), людина його не
 * бачить, клік не влучає. antd-`Button` — `forwardRef` без такої обгортки,
 * тож якір стабільний.
 *
 * Вигляд і поведінка — як у ядрового «⋮»: та сама іконка, розмір, клас
 * `button-group-item`, відкриття наведенням, `bottomRight`.
 */
const VerificationActionsMenu = observer(({ source }: VerificationActionsMenuProps) => {
    const { store, deps } = source
    const transitions = allowedTransitions(deps)

    if (transitions.length === 0) return null

    const items: MenuProps['items'] = transitions.map((transition) => {
        const { icon, danger } = BUTTONS[transition]

        return {
            key: transition,
            label: LABELS[transition],
            icon,
            danger,
            disabled: store.pending !== null,
            onClick: () => store.start(transition),
        }
    })

    return (
        <Dropdown menu={{ items }} placement="bottomRight">
            <Button
                icon={<IoMdMore style={{ fontSize: 22 }} />}
                className="button-group-item"
                size="middle"
                aria-label={T.actionsMenu}
            />
        </Dropdown>
    )
})

/**
 * Діалог «В чорний список» (`SPEC-009` §8.6): перехід знімає дані
 * верифікації (КЛ-21), тому — лише з підтвердженням і поясненням наслідку.
 */
const BlacklistConfirm = observer(({ store }: { store: VerificationStore }) => (
    <Modal
        title={store.config.texts.blacklistConfirmTitle}
        open={store.blacklistOpen}
        okText={LABELS.blacklist}
        cancelText={T.close}
        okButtonProps={{ danger: true, loading: store.pending === 'blacklist' }}
        onOk={() => store.confirmBlacklist()}
        onCancel={() => store.closeBlacklist()}
        destroyOnHidden
    >
        {store.config.texts.blacklistConfirmText}
    </Modal>
))

/** Модалки переходів — монтуються поруч із шухлядою через `content` ядра. */
const VerificationDialogs = ({ store }: { store: VerificationStore }) => (
    <>
        <VerifyModal store={store} />
        <RejectVerificationModal store={store} />
        <BlacklistConfirm store={store} />
    </>
)

/**
 * Кнопки верифікації в шапці картки власника (`SPEC-008` §7.2, `SPEC-009`
 * §8.6) — пункти випадного списку «⋮», як у легасі
 * (`VerificationActionsMenu`).
 *
 * Видимість — **лише** `_permissions` запису: Rule власника вже врахував і
 * роль, і дозволеність переходу з поточного стану (`SPEC-003` §1.3). Ролей
 * фронт не питає — легасі робив це в `useButtons4DrawerExtra.jsx:83-110`, і
 * кнопка розходилася з беком (D-11).
 *
 * Лише в перегляді: перехід із незбереженими правками форми пішов би з
 * базовими даними картки, а не з тими, що на екрані.
 *
 * Підключення — у ресурсі власника:
 * `detailConfig.modeConfig.view.onButtons = createVerificationButtons(config)`.
 */
export const createVerificationButtons =
    (config: CrmVerificationOwnerConfig) =>
    ({ buttons, crudContext, content }: VerificationButtonsParams): void => {
        if (crudContext === null || typeof crudContext !== 'object') return

        const store = verificationStoreFor(crudContext as VerificationCrudContextLike, config)
        const depStore = { formContext: (crudContext as VerificationCrudContextLike).formContext }

        // Одна позиція групи — «⋮» праворуч від «Редагувати» (sort 1000)
        buttons['verification'] = {
            priority: 'button',
            sort: 2000,
            depStore,
            depFn: (deps: ButtonDeps) => allowedTransitions(deps).length > 0,
            children: (
                <VerificationActionsMenu
                    key="crm-verification-actions"
                    source={new VerificationMenuSource(store, depStore)}
                />
            ),
        }

        content?.push(<VerificationDialogs key="crm-verification-dialogs" store={store} />)
    }
